import assertNever from "assert-never";
import * as React from "react";
import { isLoading, Loading } from "../../Loading";
import { SimplifiedAgencies } from "../../messages/Agency";
import { CaregiverStatus } from "../../messages/CaregiverStatus";
import { AgencyId } from "../../messages/ObjectIds";
import { STRINGS } from "../../strings";
import { Agency } from "../../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { translate } from "../../translate";
import { getDeviceData } from "../../utils/DeviceUtils";
import { FormControl } from "../../utils/FormControl";
import { FormGroup } from "../../utils/FormGroup";
import { Validators } from "../../utils/FormValidators";
import { HashMap } from "../../utils/HashMap";
import { phoneNumbersString } from "../../utils/PhoneNumber";
import { withDefault } from "../../utils/withDefault";
import { AppVersion } from "../../version";
import { AppButton } from "../Generic/AppButton";
import { AppDialPad, DialPadButton } from "../Generic/AppDialPad";
import { AppTextButton } from "../Generic/AppTextButton";
import { ScreenSkeleton } from "../ScreenSkeleton";
import { simpleAlert } from "../SimpleAlert";
import { AgencyNotFoundBottomSheet } from "./components/AgencyNotFoundBottomSheet";
import { ContactAgencyBottomSheet } from "./components/ContactAgencyBottomSheet";
import { RequestAgencyBottomSheet } from "./components/RequestAgencyBottomSheet";

export type AgencyRequestScreenData =
  | AgencyRequestScreenData.Empty
  | AgencyRequestScreenData.ShowError
  | AgencyRequestScreenData.ShowAgency;

export declare namespace AgencyRequestScreenData {
  export interface Empty {
    type: "Empty";
  }

  export interface ShowError {
    type: "ShowError";
    errorMessage: string;
  }

  export interface ShowAgency {
    type: "ShowAgency";
    agency: SimplifiedAgencies;
  }
}

export interface AgencyRequestScreenProps {
  data: AgencyRequestScreenData;
  caregiver: AppCaregiver | Loading;
  agencies: HashMap<AgencyId, Agency> | Loading;
  onBackClick: () => void;
  onAgencySearch: (code: string) => Promise<SimplifiedAgencies | undefined | null>;
  onAgencyRequest: (id: AgencyId) => void;
  getAnomalies: () => Promise<String>;
  onAgencyReset: () => void;
}

export interface AgencyRequestScreenState {
  code: string;
  isContactBottomSheetOpen: boolean;
  contactForm: FormGroup;
}

export class AgencyRequestScreen extends React.Component<
  AgencyRequestScreenProps,
  AgencyRequestScreenState
> {
  constructor(props: AgencyRequestScreenProps) {
    super(props);

    const contactForm = new FormGroup({
      agencyName: new FormControl(null, [Validators.required]),
      coordinatorEmail: new FormControl(null, [Validators.email]),
      coordinatorPhone: new FormControl(null),
    });

    this.state = {
      code: "",
      isContactBottomSheetOpen: false,
      contactForm: contactForm,
    };
  }

  shouldComponentUpdate(nextProps: AgencyRequestScreenProps, nextState: AgencyRequestScreenState) {
    return (
      this.props.data.type !== nextProps.data.type ||
      this.state.code !== nextState.code ||
      this.state.contactForm !== nextState.contactForm ||
      this.state.isContactBottomSheetOpen !== nextState.isContactBottomSheetOpen
    );
  }

  handleCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newCode = event.target.value;
    this.setState((_prevState: AgencyRequestScreenState) => {
      return {
        code: newCode,
      };
    });
  }

  handleDialPadButtonClick(button: DialPadButton): void {
    this.setState((state) => ({
      code: button === "backwards" ? state.code.slice(0, -1) : `${state.code}${button}`,
    }));
  }

  render() {
    return (
      <ScreenSkeleton
        title=""
        titleBarStyle={ScreenSkeleton.TitleBarStyle.TRANSPARENT}
        onBackClick={this.props.onBackClick}
        rootClassName="AgencyRequestScreen__background"
        className="AgencyRequestScreen"
      >
        {this.renderBody()}
      </ScreenSkeleton>
    );
  }

  renderBody() {
    if (this.isComponentLoading()) {
      return;
    }

    return (
      <>
        {this.renderCodeInput()}
        {this.renderDialPad()}
        {this.renderContactBottomSheet()}
        {this.renderRequestBottomSheet()}
        {this.renderAgencyNotFoundBottomSheet()}
      </>
    );
  }

  renderCodeInput() {
    return (
      <div className="AgencyRequestScreen__codeInputContainer fade-in">
        <h3>{translate(STRINGS.TYPE_AGENCY_CODE)}</h3>
        <input
          className={
            "AgencyRequestScreen__codeInput " +
            (this.props.data.type === "ShowError" ? "AgencyRequestScreen__codeInputError" : "")
          }
          type="number"
          pattern="[0-9]*"
          placeholder="1234"
          min="0"
          max="999999999"
          maxLength={9}
          value={this.state.code}
          onChange={(e) => this.handleCodeChange(e)}
          disabled={true}
        />

        <AppButton color="white" onClick={() => this.props.onAgencySearch(this.state.code)}>
          {translate(STRINGS.SEARCH_AGENCY)}
        </AppButton>

        <AppTextButton color="white" onClick={() => this.handleNoCodeClick()}>
          {translate(STRINGS.I_DONT_HAVE_MY_CODE)}
        </AppTextButton>
      </div>
    );
  }

  renderDialPad() {
    return <AppDialPad onClickButton={(button) => this.handleDialPadButtonClick(button)} />;
  }

  renderContactBottomSheet() {
    return (
      <ContactAgencyBottomSheet
        isOpen={this.state.isContactBottomSheetOpen}
        form={this.state.contactForm}
        onChange={(form) => this.handleContactFormChange(form)}
        onSubmit={(form) => this.handleContactFormSubmit(form)}
        onRequestClose={() => this.setState({ isContactBottomSheetOpen: false })}
      />
    );
  }

  renderRequestBottomSheet() {
    if (this.props.data.type !== "ShowAgency") {
      return null;
    }

    const { agency } = this.props.data;

    return (
      <RequestAgencyBottomSheet
        isOpen={true}
        agency={agency}
        onClickYes={() => this.props.onAgencyRequest(agency.agencyId)}
        onRequestClose={() => this.props.onAgencyReset()}
      />
    );
  }

  renderAgencyNotFoundBottomSheet() {
    return (
      <AgencyNotFoundBottomSheet
        isOpen={this.props.data.type === "ShowError"}
        onRequestHelp={() => this.handleNoCodeClick()}
        onRequestClose={() => this.props.onAgencyReset()}
      />
    );
  }

  isComponentLoading() {
    return isLoading(this.props.caregiver) || isLoading(this.props.agencies);
  }

  handleNoCodeClick() {
    this.setState({ isContactBottomSheetOpen: true });
  }

  private handleContactFormChange(form: FormGroup) {
    this.setState({
      contactForm: form,
    });
  }

  private async handleContactFormSubmit(form: FormGroup) {
    if (!form.isValid()) {
      return;
    }

    if (isLoading(this.props.caregiver) || isLoading(this.props.agencies)) {
      throw new Error("caregiver/agencies should not be loading");
    }

    const caregiver = this.props.caregiver;
    const agencies = this.props.agencies;
    const { agenciesString, summary } = parseAgencyAndStatusToEmailCode(agencies.values());
    const agencyNameCtrl = form.controls.get("agencyName");
    const agencyName = agencyNameCtrl !== undefined ? agencyNameCtrl.value : "my agency";
    const coordinatorEmail = form.controls.get("coordinatorEmail");
    const coordinatorPhone = form.controls.get("coordinatorPhone");
    const caregiverFullName = [caregiver.firstName, caregiver.lastName].join(" ");
    const recipients = ["info+agencycodehelp@medflyt.com"];
    if (coordinatorEmail !== undefined && typeof coordinatorEmail.value === "string") {
      recipients.push(coordinatorEmail.value);
    }
    const subjectPrefix = agencyNameCtrl !== undefined ? `from ${agencyName}` : "";
    const subjectSuffix = ", I'm looking for cases in Medflyt app";
    const subject = encodeURIComponent(`${caregiverFullName} ${subjectPrefix} ${subjectSuffix}`);
    const body = `
        I downloaded this app to get cases from ${agencyName} but i see no cases. %0D%0A
        Can you please contact my agency and activate Medflyt for me so i can see
        cases in the app?
        Thank you.%0D%0A
        %0D%0A
        Name: ${caregiverFullName} %0D%0A
        Version: ${AppVersion} %0D%0A
        Phone: ${phoneNumbersString(caregiver.phoneNumbers)} %0D%0A
        Device: ${getDeviceData()} %0D%0A
        Agencies: ${agenciesString} %0D%0A
        Coordinator Phone: ${withDefault(coordinatorPhone, new FormControl("Unspecified"))} %0D%0A
        %0D%0A
        ${summary + (await this.props.getAnomalies())}
        `;

    window.location.href = `mailto:${recipients.join(",")}?subject=${subject}&body=${body}`;

    setTimeout(() => {
      simpleAlert({ icon: "success", message: translate(STRINGS.WELL_BE_IN_TOUCH_SHORTLY) });
    }, 3000);
  }
}

export function parseAgencyAndStatusToEmailCode(agencies: Iterable<Agency>): {
  agenciesString: string;
  summary: string;
} {
  const finalMap = [];
  const statusSummaryMap: StatusSummaryMap = {};
  for (const agency of agencies) {
    if (AgencyId.unwrap(agency.id) !== 56) {
      const status = agencyStatusToIndication(agency.status);
      finalMap.push(agency.agency.name + " - " + status);
      statusSummaryMap[status] = (statusSummaryMap[status] ?? 0) + 1;
    }
  }
  const agenciesString = finalMap.length === 0 ? "-None-" : finalMap.join(",");
  let summary = "";
  for (const key of Object.keys(statusSummaryMap)) {
    const fuckTS = key as ShortenedStatus;
    summary += key + (statusSummaryMap[fuckTS] as number);
  }

  return { agenciesString: agenciesString, summary: summary };
}

type StatusSummaryMap = { [key in ShortenedStatus]?: number };
type ShortenedStatus = "P" | "A" | "S" | "T" | "J" | "ON" | "OL" | "Q" | "R";

function agencyStatusToIndication(status: CaregiverStatus): ShortenedStatus {
  switch (status) {
    case "PENDING":
      return "P";
    case "ACTIVE":
      return "A";
    case "SUSPENDED":
      return "S";
    case "TERMINATED":
      return "T";
    case "JOIN_REQUEST":
      return "J";
    case "ON_HOLD":
      return "ON";
    case "ON_LEAVE":
      return "OL";
    case "QUIT":
      return "Q";
    case "REJECTED":
      return "R";
    default:
      return assertNever(status);
  }
}
